import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { electronApis } from '@/features/electron-api';
import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';
import {
  AXON_MCP_SERVERS_QUERY_KEY,
  useMcpServersQuery,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import type {
  ConnectionTestProblem,
  ConnectionTestResult,
  ServerEntryInput,
  ServerTransport,
} from '@littlebird/axon-mcp';

/** What a catalog server's own connect dialog fills in, tests, and saves */
export interface TestedConnectSpec<Connection, Problem> {
  templateId: string;
  initialConnection: Connection;
  isComplete: (connection: Connection) => boolean;
  buildTransport: (
    entry: OnDeviceMcpCatalogEntry,
    connection: Connection
  ) => ServerTransport;
  classifyFailure: (result: ConnectionTestResult) => Problem;
}

type TestedConnectStatus = 'idle' | 'testing' | 'passed' | 'failed' | 'saving';

export type TestedConnectProblem<Problem> =
  | Problem
  | ConnectionTestProblem
  | 'save_failed'
  | 'already_connected';

interface UseTestedConnectOptions<Connection, Problem> {
  spec: TestedConnectSpec<Connection, Problem>;
  catalogEntry: OnDeviceMcpCatalogEntry;
  onConnected?: () => void;
}

export function useTestedConnect<Connection, Problem>({
  spec,
  catalogEntry,
  onConnected,
}: UseTestedConnectOptions<Connection, Problem>) {
  const queryClient = useQueryClient();
  const servers = useMcpServersQuery().data;
  const attemptRef = useRef(0);
  const [connection, setConnection] = useState(spec.initialConnection);
  const [status, setStatus] = useState<TestedConnectStatus>('idle');
  const [problem, setProblem] = useState<TestedConnectProblem<Problem> | null>(
    null
  );
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    null
  );

  useEffect(
    () => () => {
      attemptRef.current += 1;
    },
    []
  );

  const busy = status === 'testing' || status === 'saving';

  // Connect saves what was tested, so any edit invalidates a passed test
  const changeConnection = (changes: Partial<Connection>) => {
    attemptRef.current += 1;
    setConnection((current) => ({ ...current, ...changes }));
    setStatus('idle');
    setProblem(null);
    setTestResult(null);
  };

  const test = async () => {
    if (!spec.isComplete(connection) || busy) return;
    const attempt = ++attemptRef.current;
    const fail = (nextProblem: Problem | ConnectionTestProblem) => {
      if (attemptRef.current !== attempt) return;
      setStatus('failed');
      setProblem(nextProblem);
    };
    setStatus('testing');
    setProblem(null);
    try {
      const result = await electronApis.axonMcp.testServer(
        spec.buildTransport(catalogEntry, connection),
        catalogEntry.name,
        spec.templateId
      );
      if (attemptRef.current !== attempt) return;
      if (!result.ok) {
        fail(spec.classifyFailure(result));
        return;
      }
      setTestResult(result);
      setStatus('passed');
    } catch {
      fail('connection_failed');
    }
  };

  const connect = async () => {
    if (status !== 'passed' || !testResult) return;
    if (
      servers?.some((server) => templateIdForServer(server) === spec.templateId)
    ) {
      setStatus('failed');
      setProblem('already_connected');
      return;
    }

    const input: ServerEntryInput = {
      name: catalogEntry.name,
      description: catalogEntry.description,
      template: spec.templateId,
      transport: spec.buildTransport(catalogEntry, connection),
      lastConnectionTest: {
        ok: true,
        ...(testResult.serverName && { serverName: testResult.serverName }),
        toolCount: testResult.toolCount ?? 0,
        testedAt: new Date().toISOString(),
      },
    };
    setStatus('saving');
    try {
      const updatedServers = await electronApis.axonMcp.addServer(input);
      queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, updatedServers);
    } catch {
      // Keep the passed test so Connect can retry the save alone
      setStatus('passed');
      setProblem('save_failed');
      return;
    }
    onConnected?.();
  };

  return {
    connection,
    changeConnection,
    status,
    busy,
    problem,
    testResult,
    canTest: spec.isComplete(connection) && !busy,
    canConnect: status === 'passed',
    canCancel: status !== 'saving',
    test,
    connect,
  };
}

export type TestedConnectForm<Connection, Problem> = ReturnType<
  typeof useTestedConnect<Connection, Problem>
>;
